/**
 * TrustTabs — the nav across every /app/trusts/$id/* route.
 *
 * Uses TanStack Router Link with active detection via `activeTab`.
 *
 * ── Tier-aware since 2026-08-18 ──────────────────────────────────────────────
 * Tiered per-trust READ access shipped in `b1348ae0`: a beneficiary-tier member
 * now reads only `identity`, `distributions-own` and `compliance-own`, enforced
 * across 37 handlers. This nav had not caught up, so a beneficiary was offered
 * fifteen tabs of which twelve returned 403 — a wall of errors instead of a
 * smaller app.
 *
 * Each tab declares the SURFACE its screen reads, and a tab whose surface the
 * caller cannot read is not rendered. Hiding beats disabling: a greyed-out
 * "Documents" tab still tells a beneficiary the vault exists and that they are
 * being kept out of it, which is a different (and worse) message than a portal
 * that simply is what it is for them.
 *
 * The surfaces come from the backend on the trust itself (`readableSurfaces`),
 * so this file holds the tab-to-surface MAP but never the policy. When absent
 * (older backend) `canReadSurface` fails open and every tab is offered.
 *
 * ── GROUPED, ON PAUL'S RULING (2026-09-15) ───────────────────────────────────
 *
 * THE PROBLEM. Fifteen tabs do not fit one row at 1440. The design handoff
 * groups them into eight plus a `More` overflow, and takes `overflow-x: auto`
 * so a narrow viewport scrolls. Both halves of that were checked against what
 * this repo already decided:
 *
 *   · THE STRIP KEEPS WRAPPING. The grader killed the scrolling strip on
 *     5 September because it hid Tax, Risk Rating, Audit and Settings behind a
 *     scrollbar (GAP-ANALYSIS X2). A tab a reader cannot see does not exist.
 *     No `overflow-x` rule here, at any width.
 *   · THE GROUPING IS BY READ TIER. Paul, ruling 2 of 15 September: "keep the
 *     read-tier filtering. Group only tabs that share one read tier; a
 *     beneficiary never sees a combined tab that half works." The handoff's own
 *     three groups break that rule in two places, and it matters: it puts
 *     `LoA & LoW` (`documents`) inside Governance (`governance`), and folds
 *     Tax (`compliance-own`), Risk Rating (`compliance`) and Audit
 *     (`governance`) into one Compliance tab. A beneficiary reads
 *     `compliance-own` and neither of the other two, so that one tab would
 *     have shown them a group of which two thirds 403s.
 *
 * SO THE GROUPS ARE THE SURFACES. One group per read surface, which lands on
 * exactly EIGHT groups (Overview · People · Assets · Distributions ·
 * Documents · Governance · Tax · Risk rating) — the handoff's own count,
 * reached by a rule instead of by eye. A group is visible only when the reader
 * can read its surface, so a beneficiary is offered exactly their three and
 * every one of them works end to end. There is no `More` overflow, because
 * eight groups is what the partition produces and an overflow menu holding
 * nothing is a control that lies about having contents.
 *
 * NOTHING BECAME UNREACHABLE. A group with more than one destination renders a
 * second-level strip of its members when you are inside it, so every one of the
 * fifteen screens is at most two clicks from anywhere (design gate B1), and the
 * screen you are standing on is always named. `TrustTabs` keeps the same two
 * props, so no route file changed.
 */

import { Link } from '@tanstack/react-router';
import { useTrustSurfaces, type TrustReadSurface } from '../../lib/api/trusts';

interface Tab {
  id: string;
  label: string;
  to: string;
}

/**
 * The groups, in reading order. Each carries ONE read surface, and every tab in
 * it depends on that surface and nothing else — which is what makes the
 * grouping safe under the tier gate. `surface: undefined` is the overview,
 * which serves `identity` and every tier reads.
 *
 * Adding a screen means putting it in the group whose surface its handler
 * reads. Putting it anywhere else is the bug this shape exists to prevent.
 */
interface TabGroup {
  id: string;
  label: string;
  surface?: TrustReadSurface;
  tabs: (trustId: string) => Tab[];
}

const GROUPS: TabGroup[] = [
  {
    id: 'overview',
    label: 'Overview',
    tabs: (id) => [{ id: 'overview', label: 'Overview', to: `/app/trusts/${id}` }],
  },
  {
    id: 'people',
    label: 'People',
    surface: 'people',
    tabs: (id) => [
      { id: 'people', label: 'People & access', to: `/app/trusts/${id}/people` },
      { id: 'beneficiaries', label: 'Beneficiaries', to: `/app/trusts/${id}/beneficiaries` },
      // NEW-BO-TAB-ABBREV (round 11): the tab read "BO". Its own screen spells
      // it out ("TPCA REG 3C / Beneficial ownership"), so the abbreviation
      // existed only in the navigation, which is the one place the reader has
      // to guess from. Design gate E4.
      { id: 'beneficial-ownership', label: 'Beneficial ownership', to: `/app/trusts/${id}/beneficial-ownership` },
    ],
  },
  {
    id: 'assets',
    label: 'Assets',
    surface: 'financials',
    // §16 (Wave C): recognised assets as first-class records (owned-vs-pending
    // + assign/move + the trust→company→asset ownership chain). Portfolios is
    // the listed-holdings register and shares the same surface.
    tabs: (id) => [
      { id: 'assets', label: 'Assets', to: `/app/trusts/${id}/assets` },
      { id: 'portfolios', label: 'Portfolios', to: `/app/trusts/${id}/portfolios` },
    ],
  },
  {
    id: 'distributions',
    label: 'Distributions',
    // Distributions and Tax carry the `-own` surfaces deliberately: a
    // beneficiary reads their OWN distributions and their OWN IT3(t), and the
    // handlers narrow the rows rather than refusing the route. Gating these on
    // the `-all` / `compliance` surfaces would take away the two things a
    // beneficiary is unambiguously entitled to.
    surface: 'distributions-own',
    tabs: (id) => [
      { id: 'distributions', label: 'Distributions', to: `/app/trusts/${id}/distributions` },
    ],
  },
  {
    id: 'documents',
    label: 'Documents',
    surface: 'documents',
    tabs: (id) => [
      { id: 'documents', label: 'Documents', to: `/app/trusts/${id}/documents` },
      { id: 'loa-low', label: 'LoA & LoW', to: `/app/trusts/${id}/loa-low` },
    ],
  },
  {
    id: 'governance',
    label: 'Governance',
    surface: 'governance',
    tabs: (id) => [
      { id: 'resolutions', label: 'Resolutions', to: `/app/trusts/${id}/resolutions` },
      { id: 'meetings', label: 'Meetings', to: `/app/trusts/${id}/meetings` },
      { id: 'audit', label: 'Audit', to: `/app/trusts/${id}/audit` },
      { id: 'settings', label: 'Settings', to: `/app/trusts/${id}/settings` },
    ],
  },
  {
    id: 'tax',
    label: 'Tax',
    surface: 'compliance-own',
    tabs: (id) => [{ id: 'tax', label: 'Tax', to: `/app/trusts/${id}/tax` }],
  },
  {
    id: 'risk-rating',
    label: 'Risk rating',
    surface: 'compliance',
    tabs: (id) => [
      { id: 'risk-rating', label: 'Risk Rating', to: `/app/trusts/${id}/risk-rating` },
    ],
  },
];

/** Every second-level label the strip can render, flattened. Exported so the
 *  vocabulary rules (no abbreviations in navigation, design gate E4) are
 *  testable without mounting the strip and its surface gate. */
export function trustTabLabels(trustId: string): string[] {
  return GROUPS.flatMap((g) => g.tabs(trustId)).map((t) => t.label);
}

interface TrustTabsProps {
  trustId: string;
  /** The LEAF the screen is, e.g. `beneficial-ownership`. Unchanged: every
   *  route file passes what it always passed, and the group is derived. */
  activeTab: string;
}

/** Which group holds this leaf. Null for a leaf no group claims, which is a
 *  programming error rather than a user-visible state, so the strip falls back
 *  to lighting nothing rather than guessing. */
function groupOf(activeTab: string, trustId: string): TabGroup | null {
  return (
    GROUPS.find((g) => g.tabs(trustId).some((t) => t.id === activeTab)) ?? null
  );
}

export function TrustTabs({ trustId, activeTab }: TrustTabsProps) {
  const { can } = useTrustSurfaces(trustId);
  const activeGroup = groupOf(activeTab, trustId);

  const visibleGroups = GROUPS.filter((g) => {
    if (!g.surface) return true;
    // Never hide the group the user is standing in. Deep-linking straight to a
    // screen they cannot read is the backend's 403 to answer honestly; making
    // the nav vanish underneath them would leave no way back and read as a
    // broken app rather than a refusal.
    if (activeGroup && g.id === activeGroup.id) return true;
    return can(g.surface);
  });

  // The second level: the group's own screens, when it has more than one and
  // you are inside it. This is what keeps all fifteen destinations two clicks
  // away after the grouping, and it is a plain row of links — nothing behind a
  // hover, nothing behind a menu, nothing to close (design gate B4).
  const siblings =
    activeGroup && activeGroup.tabs(trustId).length > 1 ? activeGroup.tabs(trustId) : [];

  return (
    <div
      data-testid="trust-tabs-wrap"
      style={{
        // ── THE TRUST SECTIONS RENDERED TWICE (TOV-01 · NAV-04, 2026-08-27) ──
        //
        // A frame of the 25 August walkthrough at 26:40 shows this strip AND
        // the identical fifteen destinations running down the left sidebar.
        // Paul: "it's two of the same things and it's taking up space." Beno
        // wanted the top one kept and frozen like Excel freeze panes; Waldo
        // wanted the left one kept so you never scroll back up to navigate.
        //
        // Freezing this strip answers both: the sidebar copy is gone (see
        // screen-shell.tsx) and this one no longer scrolls away, so there is
        // nothing to scroll back up TO. Paul's caveat on the call was that it
        // costs about an inch of working height, which is the other reason the
        // fifteen are now eight: one row, not two.
        //
        // The document is the scroller on these screens (measured 2026-08-18,
        // see design-system.css: every ancestor up to <body> is
        // overflow-y:visible), so this parks the strip against the viewport.
        // Below 860px `main` takes `overflow-x: hidden`, which computes
        // `overflow-y` to `auto` and makes it the containing scrollport, so the
        // strip degrades to a plain in-flow row on a phone. That is the same
        // behaviour it has always had there.
        //
        // The offset is a variable because the act-as bar sticks to the same
        // scroller at z-index 9000 and would otherwise sit on top of this strip
        // for the whole scroll, on the exact path a firm demos. It resolves to
        // 0 when no act-as bar is on the page. See design-system.css.
        position: 'sticky',
        top: 'var(--sticky-stack-top, 0px)',
        zIndex: 5,
        background: 'var(--bg)',
        marginBottom: 24,
      }}
    >
      <div
        data-testid="trust-tabs"
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--hairline)',
          // DG-F1 (grader, 5 Sep 2026): a strip that scrolled sideways hid four
          // tabs until the reader found the scrollbar. WRAP instead: one row
          // where it fits, two where it does not, nothing off-screen, and no
          // overflow rule left to fight the sticky position above.
          flexWrap: 'wrap',
        }}
      >
        {visibleGroups.map((group) => {
          const isActive = !!activeGroup && group.id === activeGroup.id;
          // A group's tab goes to its FIRST screen, which is the one the group
          // is named after.
          const first = group.tabs(trustId)[0]!;
          return (
            <Link
              key={group.id}
              to={first.to as never}
              style={{
                display: 'block',
                background: 'transparent',
                padding: '10px 14px',
                fontFamily: 'var(--sans)',
                fontSize: 13.5,
                color: isActive ? 'var(--ink)' : 'var(--ink-3)',
                fontWeight: isActive ? 500 : 400,
                whiteSpace: 'nowrap',
                borderBottom: isActive
                  ? '2px solid var(--accent-400)'
                  : '2px solid transparent',
                marginBottom: -1,
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'color 160ms var(--ease-cushion)',
              }}
            >
              {group.label}
            </Link>
          );
        })}
      </div>

      {siblings.length > 0 ? (
        <div
          data-testid="trust-tabs-sub"
          aria-label={`${activeGroup?.label ?? ''} sections`}
          style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            padding: '10px 0 2px',
          }}
        >
          {siblings.map((tab) => {
            const isActive = tab.id === activeTab;
            return isActive ? (
              <span
                key={tab.id}
                aria-current="page"
                style={{
                  padding: '4px 11px',
                  borderRadius: 999,
                  fontFamily: 'var(--sans)',
                  fontSize: 12.5,
                  fontWeight: 500,
                  background: 'var(--surface-2)',
                  color: 'var(--ink)',
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.label}
              </span>
            ) : (
              <Link
                key={tab.id}
                to={tab.to as never}
                style={{
                  padding: '4px 11px',
                  borderRadius: 999,
                  fontFamily: 'var(--sans)',
                  fontSize: 12.5,
                  fontWeight: 400,
                  color: 'var(--ink-3)',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  transition: 'background 160ms var(--ease-cushion)',
                }}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
